import React, { FC } from 'react';

import { Flex } from '@chakra-ui/react';

import { useQuery } from 'react-query';
import { getStatusCount, getCasesCount } from 'api/items';
import { getEquipmentStatusCount } from 'api/equipment';

import { washHub } from 'store/slices';

import defaultStatCardStyle from './styles';
import statusToShowSettings, {
  altFilters,
  casesFitlers,
  equipmentFilters,
  packagesFilters,
  scansFilters,
} from './settings';

import { sanitizerStatusLabelText } from './utils';

import { Status } from 'types/items';

import StatCardView from './StatCard.view';

import { StatCardProps } from './StatCard.props';
import { getOrderCounts } from 'api/order';
import { getPickupCounts } from 'api/pickup';

type Type = FC<
  Pick<StatCardProps, 'selected' | 'onSelectStatus' | 'hideValue'> & {
    type: 'orders' | 'pickups' | 'items' | 'cases' | 'packages' | 'equipment';
    isScans?: boolean;
  }
>;

export const useCountQuery = (type?: string, defaultWashhub?: string) => {
  const washHubId = defaultWashhub ?? washHub.selectors.useSelectedWashHubId();

  const { data: itemsCount } = useQuery(
    `fetch-items-status-count-${washHubId}`,
    () => getStatusCount(washHubId),
    {
      enabled: type === 'items',
    },
  );

  const { data: casesCount } = useQuery(
    `fetch-cases-status-count-${washHubId}`,
    () => getCasesCount(washHubId),
    {
      enabled: type === 'cases',
    },
  );

  const { data: ordersCount } = useQuery(
    `fetch-orders-status-count-${washHubId}`,
    () => getOrderCounts(washHubId),
    {
      enabled: type === 'orders',
    },
  );

  const { data: pickupsCount } = useQuery(
    `fetch-pickups-status-count-${washHubId}`,
    () => getPickupCounts(washHubId),
    {
      enabled: type === 'pickups',
    },
  );

  const { data: equipmentCount } = useQuery(
    `fetch-equipment-status-count-${washHubId}`,
    () => getEquipmentStatusCount(washHubId),
    {
      enabled: type === 'equipment' && Boolean(washHubId),
    },
  );

  const countData =
    type === 'equipment'
      ? equipmentCount
      : type === 'orders'
      ? ordersCount
      : type === 'pickups'
      ? pickupsCount
      : type === 'items'
      ? itemsCount
      : casesCount;

  return countData;
};

const StatCardContainer: Type = ({
  selected,
  onSelectStatus,
  type,
  hideValue,
  isScans = false,
}) => {
  const data = useCountQuery(type);

  const items = ['pickups', 'orders'].includes(type)
    ? altFilters
    : type === 'cases'
    ? casesFitlers
    : type === 'packages'
    ? packagesFilters
    : type === 'equipment'
    ? equipmentFilters
    : isScans
    ? scansFilters
    : statusToShowSettings;

  return (
    <Flex
      style={{
        ...defaultStatCardStyle.container,
        justifyContent:
          ['pickups', 'orders'].includes(type) ||
          type === 'cases' ||
          type === 'packages' ||
          type === 'equipment'
            ? 'flex-start'
            : 'space-between',
      }}>
      {items.map((statusCard) => {
        const sanitizedLabel = sanitizerStatusLabelText(
          Status[statusCard.statusID],
          ['items', 'cases', 'equipment'].includes(type),
        );

        return (
          <StatCardView
            key={statusCard.statusID}
            statusLabel={sanitizedLabel}
            color={statusCard.color}
            Icon={statusCard.Icon}
            statValue={data ? data[statusCard.statusID] : 0}
            selected={selected}
            onSelectStatus={onSelectStatus}
            _bg={statusCard.bg}
            statusId={statusCard.statusID}
            hideValue={hideValue}
            type={type}
          />
        );
      })}
    </Flex>
  );
};

export default StatCardContainer;
